import React, { useEffect } from 'react';
import { C } from '../albumColors';

/**
 * Toast inline mínimo. No depende de librerías externas.
 *
 * Uso:
 *   const [toast, setToast] = useState(null); // {message, variant} | null
 *   setToast({message: 'Listo', variant: 'success'});
 *   <Toast toast={toast} onClose={() => setToast(null)} />
 *
 * variant: 'success' | 'error' | 'info'.
 */
function Toast({ toast, onClose, duration = 2800 }) {
  const hasAction = !!(toast && toast.actionLabel && toast.onAction);
  const effectiveDuration = hasAction ? Math.max(duration, 6000) : duration;
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => onClose?.(), effectiveDuration);
    return () => clearTimeout(id);
  }, [toast, onClose, effectiveDuration]);

  if (!toast) return null;
  const { message, variant = 'success', actionLabel, onAction } = toast;

  const bg = variant === 'error' ? C.error
    : variant === 'info' ? C.onSurface
    : C.primaryDim;
  const icon = variant === 'error' ? 'error'
    : variant === 'info' ? 'info'
    : 'check_circle';

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[130] flex items-center gap-2 px-4 py-2.5 rounded-full shadow-2xl"
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0) + 16px)',
        backgroundColor: bg,
        color: 'white',
        animation: 'cm-toast-in 200ms ease-out',
        maxWidth: '92vw',
      }}
      role="status"
      aria-live="polite"
    >
      <style>{`
        @keyframes cm-toast-in { from { opacity:0; transform: translate(-50%, 14px); } to { opacity:1; transform: translate(-50%, 0); } }
      `}</style>
      <span className="material-symbols-outlined text-base">{icon}</span>
      <span className="text-xs font-bold">{message}</span>
      {hasAction && (
        <button
          type="button"
          onClick={() => { onAction(); onClose?.(); }}
          className="ml-1 px-2 py-1 rounded-full text-[11px] font-bold underline hover:opacity-80 transition-opacity"
          style={{ color: 'white' }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export default Toast;
